<template>
  <header ref="headerRef" class="header" :class="{ 'header--dark': darkMode, 'header--scrolled': y > 75 }">
    <div class="header__container w-full max-w-full px-3">
      <div class="header__content w-full flex items-center gap-6">
        <div class="header__logo-section">
          <NuxtLink :to="useLocalizedPath('/')" class="header__logo block relative !h-[50px] !w-[246px]" aria-label="Home" :title="t('header.logo.title')" @click="handleLogoClick">
            <SharedPicture
              :media="siteOptions.siteLogo"
              :class="{ 'opacity-100': !darkMode, 'opacity-0': darkMode }"
              class="header__logo-image block absolute top-0 left-0 w-full h-full object-contain object-center"
              :quality="100"
              loading="eager"
              fetchpriority="high"
              height="50"
              width="246"
              alt="Herbert Seidl Logo"
              :show-placeholder="false"
            />
            <SharedPicture
              :media="siteOptions.invertedSiteLogo"
              :class="{ 'opacity-0': !darkMode, 'opacity-100': darkMode }"
              class="header__logo-image block absolute top-0 left-0 object-contain object-center"
              :quality="100"
              loading="eager"
              fetchpriority="high"
              height="50"
              width="246"
              alt="Herbert Seidl Logo"
              :show-placeholder="false"
            />
          </NuxtLink>
          <!-- INFO: Only german language for now -->
          <!-- <HeaderLanguageSwitcher /> -->
        </div>

        <div class="header__menu ml-auto">
          <HeaderMenu :menu-items="siteOptions?.headerMenu?.menuItems || []" :darkMode="darkMode" />
        </div>
        <div class="header__buttons-container hidden lg:flex gap-4 items-center w-max">
          <SharedIconButton variant="secondary" size="small" class="w-max" :title="t('menu.favoritesLinkLabel')" :to="useLocalizedPath('/merkzettel')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 4.59499C10.9104 3.59309 9.48419 3.03703 8.00398 3.03699C7.22052 3.0378 6.44496 3.19355 5.72192 3.49526C4.99888 3.79696 4.34265 4.23868 3.79098 4.79499C1.43798 7.15799 1.43898 10.854 3.79298 13.207L11.125 20.539C11.295 20.838 11.623 21.031 12 21.031C12.1548 21.0295 12.3071 20.9917 12.4446 20.9207C12.5822 20.8496 12.7011 20.7473 12.792 20.622L20.207 13.207C22.561 10.853 22.561 7.15799 20.205 4.79099C19.6536 4.2357 18.9979 3.79488 18.2756 3.49387C17.5532 3.19286 16.7785 3.03759 15.996 3.03699C14.5158 3.03723 13.0897 3.59326 12 4.59499ZM18.791 6.20499C20.354 7.77599 20.355 10.23 18.793 11.793L12 18.586L5.20698 11.793C3.64498 10.23 3.64598 7.77599 5.20498 6.20899C5.96498 5.45299 6.95898 5.03699 8.00398 5.03699C9.04898 5.03699 10.039 5.45299 10.793 6.20699L11.293 6.70699C11.3858 6.79993 11.496 6.87367 11.6173 6.92398C11.7386 6.97429 11.8686 7.00019 12 7.00019C12.1313 7.00019 12.2614 6.97429 12.3827 6.92398C12.504 6.87367 12.6142 6.79993 12.707 6.70699L13.207 6.20699C14.719 4.69799 17.281 4.70199 18.791 6.20499Z"
                fill="currentColor"
              />
            </svg>
          </SharedIconButton>
          <SharedIconButton variant="secondary" size="small" class="w-max" :title="t('menu.searchLinkLabel')" :to="useLocalizedPath('/autos-kaufen')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z"
                fill="currentColor"
              />
              <path
                d="M11.412 8.58609C11.791 8.96609 12 9.46809 12 10.0001H14C14.0009 9.47451 13.8976 8.95398 13.6961 8.46857C13.4946 7.98316 13.1989 7.54251 12.826 7.17209C11.312 5.66009 8.68701 5.66009 7.17401 7.17209L8.58601 8.58809C9.34601 7.83009 10.656 7.83209 11.412 8.58609Z"
                fill="currentColor"
              />
            </svg>
          </SharedIconButton>
          <SharedButton
            :label="t('menu.contactLinkLabel')"
            :variant="darkMode ? 'secondary' : 'primary'"
            size="small"
            :to="useLocalizedPath(siteOptions.contactPage?.slug || '')"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
  import type { SiteOption } from '~/types/strapi';
  const { t } = useI18n();
  const { emit: emitEventBus } = useEventBus();

  defineProps<{
    siteOptions: SiteOption;
  }>();

  const handleLogoClick = () => {
    emitEventBus('site-logo-click');
  };

  const headerRef = ref<HTMLDivElement | null>(null);
  const { height } = useElementBounding(headerRef);
  const headerHeight = computed(() => {
    if (height.value) {
      return `${height.value - 1}px`;
    }
    return '81px';
  });

  // Watch for height changes and update CSS variable globally
  watch(
    headerHeight,
    newHeight => {
      if (import.meta.client && document?.documentElement) {
        document.documentElement.style.setProperty('--header-height', newHeight);
      }
    },
    { immediate: true }
  );

  const route = useRoute();
  const { isDark: darkMode } = useThemeState();

  const { y } = useWindowScroll();
</script>

<style lang="scss" scoped>
  .header {
    @apply py-4 bg-white flex items-center justify-between border-b-0 border-neutral-lightest;

    &--dark {
      @apply bg-primary text-white;
    }

    &--scrolled {
      @apply border-b;
    }

    &__container {
      @media (min-width: 1536px) {
        @apply container;
      }
    }
    &__content {
      @media (min-width: 1024px) and (max-width: 1290px) {
        @apply flex-wrap;

        .header__logo-section {
          @apply order-1;
        }

        .header__buttons-container {
          @apply ml-auto;
          @apply order-2;
        }

        .header__menu {
          @apply order-3;
          @apply w-full;
          @apply justify-end;
        }
      }
    }
  }
</style>
